import { useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Loader2, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  useTaxRegisterEntries,
  useRcvSync,
  TaxRegisterTable,
  SiiCredentialsModal,
  CentralizeDialog,
} from "@/features/tax-register";
import { useTaxAuthorityConfig } from "@/features/tax-authority";

function getCurrentPeriod(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  return `${y}${m}`;
}

function formatPeriodLabel(period: string): string {
  const y = period.slice(0, 4);
  const m = parseInt(period.slice(4, 6), 10);
  const months = [
    "Enero",
    "Febrero",
    "Marzo",
    "Abril",
    "Mayo",
    "Junio",
    "Julio",
    "Agosto",
    "Septiembre",
    "Octubre",
    "Noviembre",
    "Diciembre",
  ];
  return `${months[m - 1]} ${y}`;
}

function buildPeriodOptions(): { value: string; label: string }[] {
  const options: { value: string; label: string }[] = [];
  const now = new Date();
  for (let i = 0; i < 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const value = `${y}${m}`;
    options.push({ value, label: formatPeriodLabel(value) });
  }
  return options;
}

export function LibroComprasVentasPage() {
  const [fiscalPeriod, setFiscalPeriod] = useState(getCurrentPeriod);
  const [registerType, setRegisterType] = useState<"purchases" | "sales">(
    "purchases",
  );
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showCentralize, setShowCentralize] = useState(false);

  const { config } = useTaxAuthorityConfig();
  const companyRut = config?.emisor?.rut ?? "";
  const accountingConfig = config?.accountingConfig ?? null;

  const { entries, totals, isLoading, refetch } = useTaxRegisterEntries(
    fiscalPeriod,
    registerType,
  );

  const {
    triggerSync,
    needsCredentials,
    saveCredentialsAndSync,
    dismissCredentialsModal,
    isSyncing,
    isChecking,
  } = useRcvSync();

  const periodOptions = buildPeriodOptions();
  const selectedEntries = entries.filter((e) => selectedIds.has(e.id));

  // Clear selection when switching period or type
  const handlePeriodChange = (period: string) => {
    setFiscalPeriod(period);
    setSelectedIds(new Set());
  };

  const handleTypeChange = (type: "purchases" | "sales") => {
    setRegisterType(type);
    setSelectedIds(new Set());
  };

  const handleSync = async () => {
    if (!companyRut) {
      toast.error(
        "Configure el RUT de la empresa en Configuración > Impuestos",
      );
      return;
    }
    try {
      await triggerSync(fiscalPeriod, companyRut);
      toast.success("Sincronización iniciada");
      setTimeout(refetch, 5000);
      setTimeout(refetch, 15000);
      setTimeout(refetch, 30000);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error al sincronizar");
    }
  };

  const handleSaveCredentialsAndSync = async (
    rut: string,
    password: string,
  ) => {
    await saveCredentialsAndSync(rut, password, fiscalPeriod, companyRut);
    toast.success("Credenciales guardadas. Sincronización iniciada.");
    setTimeout(refetch, 5000);
    setTimeout(refetch, 15000);
    setTimeout(refetch, 30000);
  };

  const handleCentralizeSuccess = () => {
    setSelectedIds(new Set());
    refetch();
    toast.success("Registros centralizados exitosamente");
  };

  const isBusy = isSyncing || isChecking;
  const hasAccountingConfig =
    registerType === "purchases"
      ? !!(
          accountingConfig?.inputVatAccountId &&
          accountingConfig?.accountsPayableAccountId
        )
      : !!(
          accountingConfig?.outputVatAccountId &&
          accountingConfig?.accountsReceivableAccountId
        );

  return (
    <div className="container max-w-6xl py-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Libro de Compras y Ventas</h1>
        <p className="text-muted-foreground">
          Registro oficial del SII para el periodo tributario
        </p>
      </div>

      {/* Controls row */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          {/* Period selector */}
          <select
            value={fiscalPeriod}
            onChange={(e) => handlePeriodChange(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          >
            {periodOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          {/* Register type toggle */}
          <div className="flex rounded-md border border-input overflow-hidden">
            <button
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                registerType === "purchases"
                  ? "bg-primary text-primary-foreground"
                  : "bg-background hover:bg-muted"
              }`}
              onClick={() => handleTypeChange("purchases")}
            >
              Compras
              {totals && registerType === "purchases"
                ? ` (${totals.count})`
                : ""}
            </button>
            <button
              className={`px-4 py-2 text-sm font-medium transition-colors border-l ${
                registerType === "sales"
                  ? "bg-primary text-primary-foreground"
                  : "bg-background hover:bg-muted"
              }`}
              onClick={() => handleTypeChange("sales")}
            >
              Ventas
              {totals && registerType === "sales" ? ` (${totals.count})` : ""}
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Centralize button */}
          {selectedIds.size > 0 && (
            <Button
              onClick={() => setShowCentralize(true)}
              disabled={!hasAccountingConfig}
              title={
                !hasAccountingConfig
                  ? "Configure las cuentas contables en Configuración > Impuestos > Contabilidad"
                  : undefined
              }
            >
              <BookOpen className="mr-2 h-4 w-4" />
              Centralizar ({selectedIds.size})
            </Button>
          )}

          {/* Sync button */}
          <Button onClick={handleSync} disabled={isBusy} variant="outline">
            {isBusy ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Sincronizar
          </Button>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <TaxRegisterTable
          entries={entries}
          totals={totals}
          registerType={registerType}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
        />
      )}

      {/* Credentials modal */}
      <SiiCredentialsModal
        open={needsCredentials}
        onOpenChange={(open) => {
          if (!open) dismissCredentialsModal();
        }}
        onSave={handleSaveCredentialsAndSync}
      />

      {/* Centralize dialog */}
      <CentralizeDialog
        open={showCentralize}
        onOpenChange={setShowCentralize}
        entries={selectedEntries}
        registerType={registerType}
        accountingConfig={accountingConfig}
        onSuccess={handleCentralizeSuccess}
      />
    </div>
  );
}
